<template>
  <component
    :is="tag"
    :type="tag === 'button' ? type : undefined"
    :to="to"
    :href="href"
    :disabled="disabled || loading"
    :aria-busy="loading ? 'true' : undefined"
    class="banjino-btn"
    :class="[
      `banjino-btn--${variant}`,
      `banjino-btn--${size}`,
      { 'banjino-btn--block': block, 'banjino-btn--loading': loading }
    ]"
    @click="onClick"
  >
    <span v-if="loading" class="banjino-btn__spinner" aria-hidden="true"></span>
    <span class="banjino-btn__label">
      <slot />
    </span>
  </component>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  variant: {
    type: String,
    default: 'primary',
    validator: (v) => ['primary', 'secondary', 'burgundy', 'outline', 'ghost', 'tan'].includes(v)
  },
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg', 'xl'].includes(v)
  },
  type: { type: String, default: 'button' },
  to: { type: [String, Object], default: null },
  href: { type: String, default: null },
  block: Boolean,
  disabled: Boolean,
  loading: Boolean
})

const emit = defineEmits(['click'])

const tag = computed(() => {
  if (props.to) return 'router-link'
  if (props.href) return 'a'
  return 'button'
})

const onClick = (e) => {
  if (props.disabled || props.loading) {
    e.preventDefault()
    return
  }
  emit('click', e)
}
</script>

<style lang="scss" scoped>
.banjino-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: $font-ui;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  text-decoration: none;
  border: 2px solid transparent;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 200ms ease-out;
  white-space: nowrap;
  user-select: none;
  position: relative;

  // Sizes
  &--sm {
    font-size: 0.75rem;
    padding: 0.5rem 1.25rem;
    min-height: 36px;
  }
  &--md {
    font-size: 0.875rem;
    padding: 0.75rem 1.75rem;
    min-height: 44px;
  }
  &--lg {
    font-size: 1rem;
    padding: 0.95rem 2.5rem;
    min-height: 52px;
  }
  &--xl {
    font-size: 1.125rem;
    padding: 1.1rem 3rem;
    min-height: 60px;
  }

  &--block {
    display: flex;
    width: 100%;
  }

  // Variants
  &--primary {
    background: #EB661F;
    color: #ffffff;
    border-color: #EB661F;
    &:hover:not(:disabled) {
      background: #D35616;
      border-color: #D35616;
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(235, 102, 31, 0.35);
    }
  }

  &--secondary {
    background: #285338;
    color: #ffffff;
    border-color: #285338;
    &:hover:not(:disabled) {
      background: #1a3524;
      border-color: #1a3524;
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(40, 83, 56, 0.35);
    }
  }

  &--burgundy {
    background: #912e1a;
    color: #ffffff;
    border-color: #912e1a;
    &:hover:not(:disabled) {
      background: #7a2516;
      border-color: #7a2516;
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(145, 46, 26, 0.35);
    }
  }

  &--outline {
    background: transparent;
    color: #EB661F;
    border-color: #EB661F;
    &:hover:not(:disabled) {
      background: #EB661F;
      color: #ffffff;
      transform: translateY(-2px);
    }
  }

  &--ghost {
    background: transparent;
    color: #285338;
    border-color: transparent;
    &:hover:not(:disabled) {
      background: rgba(40, 83, 56, 0.08);
    }
  }

  &--tan {
    background: #d8b767;
    color: #285338;
    border-color: #d8b767;
    &:hover:not(:disabled) {
      background: #c4a553;
      border-color: #c4a553;
      transform: translateY(-2px);
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 3px solid rgba(235, 102, 31, 0.4);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }

  &--loading {
    cursor: progress;
    transform: none !important;
    box-shadow: none !important;
    // Keep the label fully readable while loading — the spinner indicates progress.
    // Previous 0.6 opacity made the label illegible against the bright orange bg.
    opacity: 1;
  }

  &__spinner {
    width: 1em;
    height: 1em;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: banjino-btn-spin 0.7s linear infinite;
  }

  &__label {
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
  }
}

@keyframes banjino-btn-spin {
  to { transform: rotate(360deg); }
}
</style>
